import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { auth as authApi, MfaRequiredError, setAuthToken, setRefreshToken } from '../lib/api';
import { Alert, Button } from '@rankhelm/ui';

export function LoginPage() {
  const { login, setUser, setAccount } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'password' | 'magic-link' | 'mfa'>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  // Check for MFA redirect (from magic link verify or password login)
  useEffect(() => {
    if (searchParams.get('mfa') === 'true') {
      const storedToken = sessionStorage.getItem('rankhelm_mfa_token');
      if (storedToken) {
        setMfaToken(storedToken);
        setMode('mfa');
        sessionStorage.removeItem('rankhelm_mfa_token');
      }
    }
  }, [searchParams]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        setMfaToken(err.mfaToken);
        setMode('mfa');
        setError('');
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.mfaChallenge({ mfa_token: mfaToken, code: mfaCode });
      setAuthToken(res.data.token);
      setRefreshToken(res.data.refresh_token);
      setUser(res.data.user);
      setAccount(res.data.account);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.requestMagicLink({ email });
      setMagicLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: 'password' | 'magic-link') => {
    setMode(newMode);
    setError('');
    setMagicLinkSent(false);
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-full.png" alt="RankHelm" className="h-10 mx-auto" />
          <p className="text-slate-400 mt-3">Sign in to your account</p>
        </div>

        {mode === 'mfa' ? (
          <form onSubmit={handleMfaSubmit} className="bg-navy-900 border border-navy-700 rounded-xl p-8 space-y-5">
            {error && <Alert variant="error">{error}</Alert>}

            <div className="text-center mb-2">
              <div className="w-12 h-12 bg-helm-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-helm-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Two-factor authentication</h3>
              <p className="text-slate-400 text-sm mt-1">Enter the 6-digit code from your authenticator app.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
              />
            </div>

            <Button type="submit" loading={loading} className="w-full">
              Verify
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => { setMode('password'); setMfaCode(''); setMfaToken(''); setError(''); }}
                className="text-helm-400 hover:text-helm-300 transition-colors text-sm"
              >
                Back to login
              </button>
            </div>
          </form>
        ) : mode === 'password' ? (
          <form onSubmit={handlePasswordSubmit} className="bg-navy-900 border border-navy-700 rounded-xl p-8 space-y-5">
            {error && <Alert variant="error">{error}</Alert>}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 focus:border-transparent"
                placeholder="you@agency.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 focus:border-transparent"
                placeholder="Enter your password"
              />
            </div>

            <Button type="submit" loading={loading} className="w-full">
              Sign in
            </Button>

            <div className="flex items-center justify-between text-sm">
              <Link to="/forgot-password" className="text-helm-400 hover:text-helm-300 transition-colors">
                Forgot password?
              </Link>
              <button
                type="button"
                onClick={() => switchMode('magic-link')}
                className="text-helm-400 hover:text-helm-300 transition-colors"
              >
                Sign in with email link
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-navy-900 border border-navy-700 rounded-xl p-8 space-y-5">
            {magicLinkSent ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-helm-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-helm-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Check your email</h3>
                <p className="text-slate-400 text-sm mb-4">
                  We sent a sign-in link to <span className="text-white font-medium">{email}</span>. Click the link to sign in.
                </p>
                <p className="text-slate-500 text-xs">The link expires in 15 minutes.</p>
              </div>
            ) : (
              <form onSubmit={handleMagicLinkSubmit} className="space-y-5">
                {error && <Alert variant="error">{error}</Alert>}

                <div className="text-center mb-2">
                  <h3 className="text-lg font-semibold text-white">Sign in with email link</h3>
                  <p className="text-slate-400 text-sm mt-1">No password needed — we'll email you a link.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 focus:border-transparent"
                    placeholder="you@agency.com"
                  />
                </div>

                <Button type="submit" loading={loading} className="w-full">
                  Send magic link
                </Button>
              </form>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={() => switchMode('password')}
                className="text-helm-400 hover:text-helm-300 transition-colors text-sm"
              >
                Sign in with password instead
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
